import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_AREAS, CONTACT } from "@/data/site";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email, source: "footer" });
      toast.success("You're on the list. Welcome to Reverence.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-brand-charcoal text-white">
      <div className="pointer-events-none absolute -bottom-10 left-0 w-full select-none text-center font-serif text-[22vw] font-bold leading-none text-white/[0.03]">
        REVERENCE
      </div>

      <div className="container-rv relative z-10 py-20">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr_1.2fr]">
          <div>
            <span className="font-serif text-2xl font-bold tracking-[0.22em]">REVERENCE</span>
            <p className="mt-5 max-w-sm text-brand-silver leading-relaxed">
              Ottawa's largest mobile detailing crew. We come to you — and leave you falling in love
              with your car all over again.
            </p>
            <div className="mt-7 flex gap-3">
              {[Instagram, Facebook].map((Icon, i) => (
                <a key={i} href="#" aria-label="social" data-testid={`footer-social-${i}`}
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-brand-silver transition-colors duration-300 hover:border-brand-red hover:bg-brand-red hover:text-white">
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="overline text-brand-red">Service Areas</h4>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-brand-silver">
              {SERVICE_AREAS.map((a) => (
                <li key={a} className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-brand-red/70" /> {a}
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-2 text-sm text-brand-silver">
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4 text-brand-red/70" /> {CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-4 w-4 text-brand-red/70" /> {CONTACT.email}</a>
            </div>
          </div>

          <div>
            <h4 className="overline text-brand-red">Join the list</h4>
            <p className="mt-6 text-sm text-brand-silver leading-relaxed">
              Member-only discounts and seasonal specials, straight to your inbox.
            </p>
            <form onSubmit={submit} className="mt-5 flex overflow-hidden rounded-full border border-white/15 bg-white/5 focus-within:border-brand-red transition-colors">
              <input
                type="email"
                required
                data-testid="footer-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent px-5 py-3.5 text-sm text-white placeholder:text-brand-silver/60 focus:outline-none"
              />
              <button data-testid="footer-subscribe-button" disabled={busy}
                className="flex items-center gap-1 bg-brand-red px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-deep-red disabled:opacity-60">
                {busy ? "…" : <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-brand-silver/70 sm:flex-row">
          <p>© {new Date().getFullYear()} Reverence Autos · Ottawa, Canada</p>
          <div className="flex gap-6">
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/refer" className="hover:text-white">Refer a Friend</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
            <Link to="/book" className="hover:text-white">Book Now</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
